/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {InfoTooltip} from '../../tooltip/InfoTooltip';
import {FaultCodesCountBadge} from './FaultCodesCountBadge';

export interface FaultCodesSectionProps {
  count: number;
  infoTooltip?: string;
  onViewDetails?: () => void;
}

const useStyles = makeStyles(() => ({
  container : {
    boxSizing      : 'border-box',
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center',
    width          : '100%',
    padding        : '16px',
    gap            : '16px',
    border         : `1px solid ${dlsColor('grey7')}`,
    borderRadius   : '8px'
  },
  left : {
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center',
    alignItems     : 'flex-start'
  },
  titleRow : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px'
  },
  title : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  },
  viewDetails : {
    cursor        : 'pointer',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('darkestBlue')
  }
}));

/**
 * FaultCodesSection is the ION-7462 Fault Codes container: a "FAULT CODES"
 * title (with an info tooltip) and a "View Details" link on the left, and
 * the always-red `FaultCodesCountBadge` on the right. Per Details.txt, this
 * whole section only renders when fault code data is available - callers
 * (e.g. `DiagnosticsAccordion`) are expected to omit this component
 * entirely rather than render it with a zero count.
 *
 * "View Details" is a plain callback (`onViewDetails`) with no built-in
 * navigation - per this epic's plan, the exact "New Fault Codes
 * interaction" mechanics (modal vs. route vs. panel swap) are an
 * explicitly open decision left to the caller.
 *
 * ```typescript
 *
 *   import {FaultCodesSection} from
 *     '@ion/dls/src/components/assetdetails/current/FaultCodesSection';
 *   ...
 *
 *   <FaultCodesSection count={20}
 *     infoTooltip='Active fault codes reported by this asset.'
 *     onViewDetails={onViewDetails} />
 *
 * ```
 *
 */
export const FaultCodesSection = ({
  count,
  infoTooltip,
  onViewDetails
}: FaultCodesSectionProps) => {
  const classes = useStyles();
  const onViewDetailsClick = useCallback(() => {
    onViewDetails?.();
  }, [onViewDetails]);
  return (
    <div className={classes.container}>
      <div className={classes.left}>
        <div className={classes.titleRow}>
          <span className={classes.title}>
            FAULT CODES
          </span>
          {infoTooltip && (
            <InfoTooltip message={infoTooltip}
              size={24}
              color={dlsColor('grey3')} />
          )}
        </div>
        <span className={classes.viewDetails}
          onClick={onViewDetailsClick}>
          View Details
        </span>
      </div>
      <FaultCodesCountBadge count={count} />
    </div>
  );
};

export default React.memo(FaultCodesSection);
